import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { ref, computed, PropType } from 'vue';
import { Line } from 'vue-chartjs';
import { ChartConfig } from './chartConfig';
import { Data, Series, SeriesConfig, SeriesOrderConfiguration } from '@/features/reports/models';
import ColourService from '@/shared/services/color';
import { ChartDataFormat } from '@/features/reports/models/chartDataFormat';
import merge from 'lodash.merge';


export default /*@__PURE__*/_defineComponent({
  __name: 'LineChart',
  props: {
  data: {
    type: Object as PropType<Data>,
    required: true,
  },
  reportId: {
    type: String,
    required: true,
  },
  seriesOrder: {
    type: Array as PropType<SeriesOrderConfiguration>,
  },
  fill: {
    type: Boolean,
    required: true,
  },
  allowDecimalTicksY: {
    type: Boolean,
    default: false,
  },
  dataFormat: {
    type: Number as PropType<ChartDataFormat>,
  },
  labelValues: {
    type: Object as PropType<Map<string, string>>,
  },
},
  setup(__props) {

const props = __props;

const renderSeriesInReverse = ref(false);

const orderedSeries = computed(() => {
  return seriesRenderOrder.value.map((config) => getSeries(config.name));
});

const seriesConfig = computed(() => {
  if (!props.seriesOrder?.length) return [];
  return typeof props.seriesOrder[0] === 'string'
    ? (props.seriesOrder as string[]).map((name) => ({ name, displayName: name }))
    : (props.seriesOrder as SeriesConfig[]);
});

const seriesRenderOrder = computed(() => {
  return renderSeriesInReverse.value ? [...seriesConfig.value].reverse() : seriesConfig.value;
});

const getSeries = (name: string): Series => {
  return props.data.series.find((series) => series.name === name)!;
};

const mapData = computed(() => {
  if (seriesConfig.value.length) {
    renderSeriesInReverse.value = true;
    return mapDataWithConfiguredSeries();
  }

  const coloursRequired = props.data.series.length;
  const borderPalette = ChartConfig.backgroundColoursForRandomItems(coloursRequired);
  const backgroundPalette = props.fill ? borderPalette.map((c) => ColourService.setAlpha(c, 0.2)) : borderPalette;

  return {
    labels: props.data.scale.labels,
    datasets: props.data.series.map((series, index) => ({
      label: series.name,
      data: series.data,
      backgroundColor: backgroundPalette[index],
      borderColor: borderPalette[index],
      borderWidth: 1,
      fill: props.fill ? (index === 0 ? true : '-1') : false,
      lineTension: 0,
      showLine: true,
      radius: 0,
    })),
  };
});

const mapDataWithConfiguredSeries = () => {
  const backgroundPalette = ChartConfig.backgroundPalette(seriesRenderOrder.value);
  const borderPalette = ChartConfig.borderPalette(seriesRenderOrder.value);

  return {
    labels: props.data.scale.labels,
    datasets: orderedSeries.value.map((series, index) => {
      const config = seriesConfig.value.find((c) => c.name === series.name) || {
        name: series.name,
        displayName: series.name,
      };
      return {
        label: config.displayName,
        data: series.data,
        backgroundColor: backgroundPalette[index],
        borderColor: borderPalette[index],
        borderWidth: 1,
        fill: props.fill ? (index === 0 ? true : '-1') : false,
        lineTension: 0,
        showLine: true,
        radius: 0,
      };
    }),
  };
};

const chartOptions = computed(() => {
  const overrides = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      inlineLabels: {
        reportId: props.reportId,
        reverseLabels: renderSeriesInReverse.value,
        dataFormat: props.dataFormat,
        defaultLabelValues: props.labelValues,
      },
      legend: {
        position: 'right',
      },
    },
    scales: {
      x: {
        title: { display: true, text: props.data.scale.title },
        ticks: { maxTicksLimit: 32 },
        grid: { display: false },
      },
      y: {
        ticks: {
          beginAtZero: true,
          callback: (value: any) => (props.allowDecimalTicksY ? value : value % 1 === 0 ? value : undefined),
          max: props.allowDecimalTicksY ? 1 : undefined,
        },
      },
    },
    legend: { reverse: renderSeriesInReverse.value },
  };

  return merge({}, ChartConfig.gridChartsGlobalOptions, overrides);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Line), {
    data: mapData.value,
    options: chartOptions.value
  }, null, 8, ["data", "options"]))
}
}

})